import { useEffect, useState } from "react";
import { GrFormNextLink, GrFormPreviousLink } from "react-icons/gr";
import { FormattedMessage, useIntl } from "react-intl";
import { FiX } from "react-icons/fi";
import { Col, Row } from "reactstrap";
import { AssistanceService } from "../../services/AssistanceService";
import ButtonComponent from "../ButtonComponent";
import { PaginatedPartListDTO } from "../../models/AssistanceParts/PaginatedPartListDTO";
import Toast from "../../helpers/Toast";
import { AssistancePartListDTO } from "../../models/AssistanceParts/AssistancePartListDTO";
import { CircularProgress } from "@mui/material";
import React from "react";
import TableComponent from "../TableComponent";
import { SortingRule } from "react-table";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { Utilities } from "../../helpers/Utilities";

type PartsRequestedTableComponentType = {
  assistanceId: number;
  mode: "workshop" | "backoffice";
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  reload: boolean;
  isComplete: boolean;
  isAcceptedBudget: boolean;
};

export function PartsRequestedTableComponent({
  mode,
  assistanceId,
  setActiveTab,
  reload,
  isComplete,
  isAcceptedBudget
}: PartsRequestedTableComponentType) {
  const assistanceService = new AssistanceService();
  const [parts, setParts] = useState<AssistancePartListDTO[]>([]);
  const [isLoadingAssistance, setIsLoadingAssistance] = useState<boolean>(false);

  const { availableModules, pageSizeOptions } = useAvailableModulesContext();

  // Define os tamanhos de página padrão
  const defaultPageSizes: number[] = [5, 15, 30];

  // Usa o Utilities para obter os tamanhos de página com base nas configurações
  const pageSizes: number[] = Utilities.GetPageSizeFromSettings(defaultPageSizes, pageSizeOptions);

  // Usa o Utilities para obter o tamanho de página inicial
  const [pageSize, setPageSize] = useState<number>(
    Utilities.GetInitialPageSize(pageSizes, pageSizeOptions, defaultPageSizes[0])
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const intl = useIntl();
  const [reloadParts, setReloadParts] = useState<boolean>(false);
  const [goToPage, setGoToPage] = useState<number>(1);

  const isLocked = isAcceptedBudget || isComplete;
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getRequestParts = async (currentPage: number, pageSize: number, sortBy: SortingRule<object>[]) => {
    if (assistanceId === 0) {
      return;
    }

    var data: PaginatedPartListDTO = {
      assistanceId: assistanceId,
      currentPage: currentPage,
      pageSize: pageSize,
    };

    setIsLoadingTable(true);
    var response = await assistanceService.GetRequestedParts(data);

    if (response.success === false) {
      setIsLoadingTable(false);
      Toast.Show("error", response.message);
      return;
    }

    setCurrentPage(currentPage);
    setPageSize(pageSize);
    setTotalPages(response.totalPages);
    setIsLoadingTable(false);
    setParts(response.items);
    setGoToPage(0);

    if (response.totalPages > 0 && currentPage > response.totalPages) {
      setGoToPage(1);
    }
  };

  const UsePart = async (assistancePartId: number) => {
    setIsLoadingTable(true);
    var response = await assistanceService.UsePart(assistancePartId);
    if (response.success === false) {
      setIsLoadingTable(false);
      Toast.Show("error", response.message);
      return;
    }
    setIsLoadingTable(false);
    Toast.Show("success", "Peça utilizada com sucesso");
    setReloadParts(prevReloadParts => !prevReloadParts);
  };

  const UseAllParts = async () => {
    setIsLoadingTable(true)
    var response = await assistanceService.UseAllParts(assistanceId);

    if (response.success === false) {
      setIsLoadingTable(false);
      Toast.Show("error", response.message);
      return;
    }

    setIsLoadingTable(false);
    Toast.Show("success", "Todas as peças utilizadas com sucesso");
    setActiveTab(0)
  }



  const columns = React.useMemo(
    () => [
      {
        Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Code" />,
        accessor: "erpId",
        width: 25,
      },
      {
        Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Name" />,
        accessor: "name",
        width: 35,
      },
      {
        Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Quantity" />,
        accessor: "quantity",
        width: 35,
      },
      {
        Header: <FormattedMessage id="Screens.Assistances.Parts.Table.PartDescription" />,
        accessor: "requestPartDescription",
        Cell: (props: any) => {
          const isExpanded = expandedRows[props.cell.row.original.id];
          return (
            <div style={{ display: "inline-flex", height: "30px" }}>
              <ButtonComponent
                text={isExpanded ? intl.formatMessage({ id: "ExecutionsTableComponent.Close" }) : intl.formatMessage({ id: "ExecutionsTableComponent.Open" })}
                onClick={() => toggleRow(props.cell.row.original.id)}
              />
            </div>
          );
        },
        width: 10,
      },
      {
        Header: "",
        accessor: " ",
        Cell: (props: any) => {
          return (
            <div style={{ display: "inline-flex", height: "30px" }}>
              <ButtonComponent
                text={intl.formatMessage({ id: "PartsRequestedTableComponent.Delivery" })}
                disabled={isLocked === false ? isLoadingTable : true}
                onClick={() => UsePart(props.cell.row.original.id)}
              />
            </div>
          );
        },
        width: 25,
      },
    ],
    [],
  );

  const renderRowSubComponent = (row: any) => {
    const part = row.original;
    return (
      <tr>
        <td colSpan={columns.length}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: "100%", padding: "0.5rem", textAlign: "left" }}>
                <p style={{ marginBottom: "8px" }}><strong>Observações:</strong></p>
                {part.requestPartDescription}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  useEffect(() => {
    getRequestParts(currentPage, pageSize, []);
  }, [reloadParts, reload]);

  return (
    <>
      <Row style={{ margin: "1rem 0px", display: "flex", justifyContent: "end", alignItems: "center" }}>
        <Col xl={3} lg={3} sm={3} xs={12} style={{ padding: "0px" }}>
          <ButtonComponent
            style={{ width: '100%' }}
            onClick={() => {
              UseAllParts()
            }}
            text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.DeliveredAll" })}
            loading={isLoadingAssistance}
            disabled={isLocked}
          />
        </Col>
      </Row>
      <TableComponent
        goToPage={goToPage}
        loading={isLoadingTable}
        pageCount={totalPages}
        columns={columns}
        manualPagination={true}
        fetchData={getRequestParts}
        pageSizes={pageSizes}
        initialPage={currentPage}
        initialPageSize={pageSize}
        initialSortBy={[]}
        data={parts}
        dontOrderFirstCollumn={true}
        renderRowSubComponent={renderRowSubComponent}
        expandedRows={expandedRows}
      />

    </>
  );
}
